import { useQuery, useQueryClient } from "@tanstack/react-query";
import { z } from "zod";
import { DbUser } from "../models";
import { BaseDbObject } from "../models/db";
import { Customer } from "./reports";
import {
  fetchApiDelete,
  fetchApiGet,
  fetchApiPost,
  fetchApiPut,
} from "./utils";
import { transformToDecimal } from "./common";
import Decimal from "decimal.js";
import { request } from "https";

export enum FundRequestStatus {
  FailedToParse = "failed_to_parse",
  AwaitingValidation = "awaiting_validation",
  Granted = "granted",
}

export const UpdateBeaconFundRequestValidation = z.object({
  id: z.string().uuid(),

  // Survey data
  survey: z.object({
    firstName: z.string().min(1, "First name is required"),
    lastName: z.string().min(1, "Last name is required"),
    phone: z.union([z.string(), z.literal("")]).optional(),
    email: z.union([z.string().email("Invalid email"), z.literal("")]).optional(),
    matchingGiftAmount: z.string().optional(),
    recipientOrganization: z.string().min(1, "Organization is required"),
    recipientOrganizationMailingAddress: z.string().optional(),
    someOrAllMatchingConfirmation: z.string().optional(),
    organizationPurpose: z.string().optional(),
  }),

  customer_id: z.string().optional().nullable(),
  employee_id: z.string().uuid().optional().nullable(),
  customer_requested: z.string().optional().nullable(),

  has_been_granted: z.boolean(),
  has_been_dispersed: z.boolean(),
  amount_granted: z.number().optional().or(z.string().optional()).transform(transformToDecimal({ key: "amount_granted" })),
}).refine((data) => {
  // If granted is true, amount must be provided
  if (data.has_been_granted && (!data.amount_granted || data.amount_granted === 0)) {
    return false;
  }
  return true;
}, {
  message: "Amount is required when request is granted",
  path: ["amount_granted"],
}).refine((data) => {
  // Disbursed can only be true if granted is true
  if (data.has_been_dispersed && !data.has_been_granted) {
    return false;
  }
  return true;
}, {
  message: "Cannot disburse without granting first",
  path: ["has_been_dispersed"],
});

export const CreateBeaconFundRequestValidation = z.object({
  // Survey data
  survey: z.object({
    firstName: z.string().min(1, "First name is required"),
    lastName: z.string().min(1, "Last name is required"),
    phone: z.union([z.string(), z.literal("")]).optional(),
    email: z.union([z.string().email("Invalid email"), z.literal("")]).optional(),
    matchingGiftAmount: z.string().optional(),
    recipientOrganization: z.string().min(1, "Organization is required"),
    recipientOrganizationMailingAddress: z.string().optional(),
    someOrAllMatchingConfirmation: z.string().optional(),
    organizationPurpose: z.string().optional(),
  }),
  
  // Request data
  customer_id: z.string().uuid().optional().nullable(),
  employee_id: z.string().uuid().optional().nullable(),
  customer_requested: z.string().optional().nullable(),

  has_been_granted: z.boolean().default(false),
  has_been_dispersed: z.boolean().default(false),
  amount_granted: z.number().optional().or(z.string().optional()),
}).refine((data) => {
  const keys = ["customer_id", "employee_id", "customer_requested"] as const;
  if (keys.every((key) => !data[key])) {
      return true;
  }

  return keys.filter((key) => !!data[key]).length === 1;
}, {
  message: "Either customer_requested or customer_id or employee_id must be provided, but not both",
  path: ["customer_id"],
}).refine((data) => {
  // If granted is true, amount must be provided
  if (data.has_been_granted && (!data.amount_granted || data.amount_granted === 0)) {
    return false;
  }
  return true;
}, {
  message: "Amount is required when request is granted",
  path: ["amount_granted"],
}).refine((data) => {
  // Disbursed can only be true if granted is true
  if (data.has_been_dispersed && !data.has_been_granted) {
    return false;
  }
  return true;
}, {
  message: "Cannot disburse without granting first",
  path: ["has_been_dispersed"],
});

export const DeleteFundRequestValidation = z.object({
  id: z.string().uuid(),
});


export const BirthdayFundBreakdownStatus = {
  Projected: "projected",
  Granted: "granted",
  Dispersed: "dispersed",
  Reconciled: "reconciled",
} as const;

export type BirthdayFundBreakdownStatus = typeof BirthdayFundBreakdownStatus[keyof typeof BirthdayFundBreakdownStatus];

export const BirthdayFundBreakdownInput = z.object({
  id: z.string().uuid().optional(),
  name: z.string(),
  amount: z.number().or(z.string()).transform(transformToDecimal({ key: "amount" })),
  status: z.enum([BirthdayFundBreakdownStatus.Projected, BirthdayFundBreakdownStatus.Granted, BirthdayFundBreakdownStatus.Dispersed, BirthdayFundBreakdownStatus.Reconciled]),
});

export const UpdateFundRequestValidation = z.object({
  id: z.string().uuid(),

  nominated_organization: z.string(),
  organization: z.string().optional(),
  nominated_name: z.string(),

  has_been_granted: z.boolean(),
  has_been_dispersed: z.boolean(),
  has_been_reconciled: z.boolean(),

  notes: z.string().optional().nullable(),

  breakdowns: z.array(BirthdayFundBreakdownInput),
});


export const UpdateAllocationDataValidation = z.object({
  id: z.string().uuid(),
  year: z.number().int().min(2000).max(2100).optional(),
  amount_allocated: z.union([z.string(), z.number(), z.null()]).optional(),
  is_discretionary: z.boolean().optional(),
  customer_id: z.union([z.string().uuid(), z.null()]).optional(),
  employee_id: z.union([z.string().uuid(), z.null()]).optional(),
});

export const CreateAllocationDataValidation = z.object({
  year: z.number().int().min(2000).max(2100),
  amount_allocated: z.union([z.string(), z.number()]),
  is_discretionary: z.boolean().optional(),
  customer_id: z.string().uuid().optional(),
  employee_id: z.string().uuid().optional(),
});

interface ParsedBeaconSurvey {
  data: {
    firstName?: string | null;
    lastName?: string | null;
    phone?: string | null;
    email?: string | null;
    matchingGiftAmount?: string | null;
    recipientOrganization?: string | null;
    recipientOrganizationMailingAddress?: string | null;
    someOrAllMatchingConfirmation?: string | null;
    organizationPurpose?: string | null;
    parsedConfirmation?: boolean | null;
  },

  errors: string[],
}

interface ParsedBirthdaySurvey {
  data: {
    nominator_first_name: string;
    nominator_last_name: string;
    nominating_organization: string;
    email: string;
    phone: string;
    birthday_month_of_child: string;
    birthday_day_of_child: string;
    child_age: string;
    party_location?: string;
    notes?: string;
  },

  errors: string[],
}

export interface BeaconFundRequest {
  id: string;
  created_at: string;
  updated_at: string;

  survey: ParsedBeaconSurvey,

  nominated_organization?: string;
  Customer?: { id: string; long_name: string; }
  Employee?: { id: string; name: string; }

  employee_id?: string;
  customer_id?: string;

  status: FundRequestStatus;
  has_been_granted: boolean;
  has_been_dispersed: boolean;
  amount_granted: string;
  granted_date?: string;

  request_date?: string;
  year: number;
}

export interface BeaconAllocation {
  id: string;
  created_at: string;
  updated_at: string;

  employee_id?: string;
  customer_id?: string;

  Customer?: { id: string; long_name: string; }
  Employee?: { id: string; name: string; }

  year: number;
  amount_allocated: string;
  is_discretionary?: boolean;
}

export interface BirthdayCertificate {
  id: string;
  created_at: string;
  updated_at: string;
  certificate_id: string; // random id that appears on the certificate PDF, not a uuid.
  location_name: string;
  status: 'sent' | 'invoiced' | 'paid';
  expiration: string; // datetime
}

export interface BirthdayFundBreakdown {
  id: string;
  name: 'Venue' | "Pizza" | "Travel" | "Cupcakes";
  amount: string;
  status: 'projected' | "granted";
  created_at: string;
  updated_at: string;

  certificates: BirthdayCertificate[];
}

export interface BirthdayFundRequest {
  id: string;
  created_at: string;
  updated_at: string;

  survey: ParsedBirthdaySurvey,

  nominated_organization: string;
  organization?: string;
  nominated_name: string;

  status: FundRequestStatus;
  has_been_granted: boolean;
  has_been_dispersed: boolean;
  has_been_reconciled: boolean;
  amount_projected: string;
  amount_granted: string;
  granted_date?: string;

  notes?: string | null;
  birthday_fund_number: number;
  expired_at?: string | null;

  breakdowns: BirthdayFundBreakdown;
}

export interface BeaconSummary {
  total_requested: string;
  total_matched: string;
  total_disbursed: string;

  employee_stats: {
    total_requested: string;
    total_matched: string;
    total_disbursed: string;
  },

  customer_stats: {
    total_requested: string;
    total_matched: string;
    total_disbursed: string;
  },
}

export interface BeaconFundListRequestsResponse {
  fund_requests: BeaconFundRequest[];
}

export interface BeaconFundListAllocationsResponse {
  allocations: BeaconAllocation[];
}

export interface BeaconFundSummaryResponse {
    full_customers_list: string[];
    full_employees_list: string[];
    total_summary: BeaconSummary;
    summary: BeaconSummary;
}

type OrganizationSummary = {
  total_requested: number;
  total_granted: number;
  amount_granted: string;
  unique_kids_nominated: number;
};

type PartnerSummary = {
  total_requested: number;
  total_granted: number;
  amount_granted: string;
  unique_kids_nominated: number;
};

type MonthSummary = {
  total_requested: number;
  total_granted: number;
  amount_granted: string;
};

type FundSummaries = {
  by_organization: Record<string, OrganizationSummary>;
  by_partner: Record<string, PartnerSummary>;
  total_requested: number;
  total_requested_including_expired: number;
  total_granted: number;
  amount_granted: string;
  amount_projected: string;
  expired_total_count: number;
  expired_amount_projected: string;
  expired_amount_granted: string;
  by_month: Record<string, MonthSummary>;
  parties_by_destination: Record<string, number>;
  birthdays_by_age: Record<string, number>;
  birthday_cost_average: string;
  birthday_cost_median: string;
  birthday_cost_count: number;
  birthday_cost_projected_average: string;
  birthday_cost_projected_median: string;
  birthday_cost_projected_count: number;
};

export const ListBeaconFiltersValidation = z.object({
  years: z.array(z.number().min(2000).max(2050)),
  customer_id: z.string().uuid().optional(),
  employee_id: z.string().uuid().optional(),
  granted: z.boolean().optional(),
  dispersed: z.boolean().optional(),
  status: z.string().optional(),
});

export interface ListRequestsResponse {
  full_organizations_list: string[];
  organizations: string[];
  fund_requests: BirthdayFundRequest[];
  summaries: FundSummaries;
}

export const ListFiltersValidation = z.object({
  granted: z.boolean().optional(),
  dispersed: z.boolean().optional(),
  status: z.string().optional(),
});

function assembleListQueryParmas(filters?: z.infer<typeof ListFiltersValidation>) {
  const queryParams = new URLSearchParams();
  if (!filters) {
    return queryParams;
  }

  if (typeof filters?.granted === 'boolean') {
    queryParams.append("has_been_granted", filters.granted.toString());
  }

  if (typeof filters?.dispersed === 'boolean') {
    queryParams.append("has_been_dispersed", filters.dispersed.toString());
  }

  if (filters.status) {
    queryParams.append("status", filters.status);
  }

  return queryParams;
}

function assembleBeaconListQueryParmas(filters?: z.infer<typeof ListBeaconFiltersValidation>) {
  const queryParams = new URLSearchParams();
  if (!filters) {
    return queryParams;
  }

  queryParams.append("years", JSON.stringify(filters.years!));

  if (typeof filters?.granted === 'boolean') {
    queryParams.append("has_been_granted", filters.granted.toString());
  }

  if (typeof filters?.dispersed === 'boolean') {
    queryParams.append("has_been_dispersed", filters.dispersed.toString());
  }

  if (filters.status) {
    queryParams.append("status", filters.status);
  }

  if (filters.employee_id) {
    queryParams.append("employee_id", filters.employee_id);
  }

  if (filters.customer_id) {
    queryParams.append("customer_id", filters.customer_id);
  }

  return queryParams;
}


export function useListBirthdayRequests({ filters }: { filters?: z.infer<typeof ListFiltersValidation> }) {
  const queryParams = assembleListQueryParmas(filters);
  return useQuery({
    queryKey: ["fund", "birthday", queryParams.toString()],
    queryFn: () => fetchApiGet<ListRequestsResponse>(`fund/birthday/requests`, queryParams),
    retry: 1,
  });
}

export function useListBirthdayRequest(request_id?: string | null) {
  return useQuery({
    queryKey: ["fund", "birthday", request_id],
    queryFn: () => fetchApiGet<BirthdayFundRequest>(`fund/birthday/requests/${request_id}`),
    retry: 1,
    enabled: !!request_id
  });
}

export function useGetBirthdayRequestDocument(request_id?: string | null) {
  return useQuery({
    queryKey: ["fund", "birthday", request_id, "raw"],
    queryFn: () => fetchApiGet<{ document_content: string }>(`fund/birthday/requests/${request_id}/original-document`),
    retry: 1,
    enabled: !!request_id
  });
}

export function useListBeaconSummary({ filters }: { filters?: z.infer<typeof ListBeaconFiltersValidation> }) {
  const queryParams = assembleBeaconListQueryParmas(filters);
  return useQuery({
    queryKey: ["fund", "beacon", "summary", queryParams.toString()],
    queryFn: () => fetchApiGet<BeaconFundSummaryResponse>(`fund/beacon/summary`, queryParams),
    retry: 1,
  });
}

export function useListBeaconRequests({ filters }: { filters?: z.infer<typeof ListBeaconFiltersValidation> }) {
  const queryParams = assembleBeaconListQueryParmas(filters);
  return useQuery({
    queryKey: ["fund", "beacon", "requests", queryParams.toString()],
    queryFn: () => fetchApiGet<BeaconFundListRequestsResponse>(`fund/beacon/requests`, queryParams),
    retry: 1,
  });
}

export function useListBeaconAllocations({ filters }: { filters?: z.infer<typeof ListBeaconFiltersValidation> }) {
  const queryParams = assembleBeaconListQueryParmas(filters);
  return useQuery({
    queryKey: ["fund", "beacon", "allocations" ,queryParams.toString()],
    queryFn: () => fetchApiGet<BeaconFundListAllocationsResponse>(`fund/beacon/allocations`, queryParams),
    retry: 1,
  });
}

export function useListBeaconFundRequest(request_id?: string | null) {
  return useQuery({
    queryKey: ["fund", "beacon", "requests", request_id],
    queryFn: () => fetchApiGet<BeaconFundRequest>(`fund/beacon/requests/${request_id}`),
    retry: 1,
    enabled: !!request_id
  });
}

export function useUpdateRequest() {
  const queryClient = useQueryClient();

  return async (params: typeof UpdateFundRequestValidation._type) => {
    const queryParams = new URLSearchParams();

    const result = await fetchApiPut<any>(
      `fund/birthday/requests/${params.id}`,
      params,
      queryParams
    );

    if (result.success) {
      queryClient.invalidateQueries({ queryKey: ["fund", "birthday"] });
    }

    return result;
  };
}

export function useDeleteRequest() {
  const queryClient = useQueryClient();

  return async (params: typeof DeleteFundRequestValidation._type) => {
    const queryParams = new URLSearchParams();

    const result = await fetchApiDelete<any>(
      `fund/birthday/requests/${params.id}`,
    );

    if (result.success) {
      queryClient.invalidateQueries({ queryKey: ["fund", "birthday"] });
    }

    return result;
  };
}

export function useExpireRequest() {
  const queryClient = useQueryClient();
  return async (params: { id: string; confirmation_acknowledged?: boolean }) => {
    const result = await fetchApiPost<any>(
      `fund/birthday/requests/${params.id}/expire`,
      { confirmation_acknowledged: params.confirmation_acknowledged }
    );
    if (result.success) { queryClient.invalidateQueries({ queryKey: ["fund", "birthday"] }); }
    return result;
  };
}

export function useUpdateBeaconRequest() {
  const queryClient = useQueryClient();

  return async (params: typeof UpdateBeaconFundRequestValidation._type) => {
    const queryParams = new URLSearchParams();

    const result = await fetchApiPut<any>(
      `fund/beacon/requests/${params.id}`,
      params,
      queryParams
    );

    if (result.success) {
      queryClient.invalidateQueries({ queryKey: ["fund", "beacon"] });
    }

    return result;
  };
}

export function useDeleteBeaconRequest() {
  const queryClient = useQueryClient();

  return async (params: { id: string }) => {
    const queryParams = new URLSearchParams();

    const result = await fetchApiDelete<any>(
      `fund/beacon/requests/${params.id}`,
    );

    if (result.success) {
      queryClient.invalidateQueries({ queryKey: ["fund", "beacon"] });
    }

    return result;
  };
}

export function useUpdateBeaconAllocation() {
  const queryClient = useQueryClient();

  return async (data: z.infer<typeof UpdateAllocationDataValidation>) => {
    const queryParams = new URLSearchParams();

    const result = await fetchApiPut<any>(
      `fund/beacon/allocations/${data.id}`,
      data,
    );

    if (result.success) {
      queryClient.invalidateQueries({ queryKey: ["fund", "beacon"] });
    }

    return result;
  };
}

export function useCreateBeaconAllocation() {
  const queryClient = useQueryClient();

  return async (data: z.infer<typeof CreateAllocationDataValidation>) => {
    const queryParams = new URLSearchParams();

    const result = await fetchApiPost<any>(
      `fund/beacon/allocations`,
      data,
    );

    if (result.success) {
      queryClient.invalidateQueries({ queryKey: ["fund", "beacon"] });
    }

    return result;
  };
}

export function useCreateBeaconRequest() {
  const queryClient = useQueryClient();

  return async (data: z.infer<typeof CreateBeaconFundRequestValidation>) => {
    const result = await fetchApiPost<BeaconFundRequest>(
      `fund/beacon/requests`,
      data,
    );

    if (result.success) {
      queryClient.invalidateQueries({ queryKey: ["fund", "beacon"] });
    }

    return result;
  };
}
